import React, { useEffect, useState } from "react"
import _ from "lodash"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from "formik"
import { useTranslation } from "react-i18next"
import {
	Box,
	FormControl,
	TextField,
	makeStyles,
	Button,
	Typography,
	Select,
	MenuItem,
	FormHelperText,
	InputAdornment,
	IconButton,
	Tooltip,
	InputLabel,
	Input,
	Checkbox,
	ListItemText,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormLabel,
} from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import * as yup from "yup"
import { KeyboardArrowDown } from "@material-ui/icons"
import VisibilityOffSVG from "../../../icons/VisibilityOffSVG"
import VisibilitySVG from "../../../icons/VisibilitySVG"
import InfoIconSVG from "../../../icons/InfoIconSVG"
import { roles } from "../../../../data/roles"
import { createUser, emailAvailabilityCheck, updateUser, updateUserReset } from "../../../../actions/usersActions"
import { clearLocalStorage, validateEmail } from "../../../../utils"
import { changePassword, changePasswordReset } from "../../../../actions/headerActions"
import { logoutClearStore, logoutUser, logoutUserAction } from "../../../../actions/userActions"
import { clearMultibase } from "../../../../actions/multiAiBase"

//const nameRegx = /^[A-Za-z][A-Za-z\'\-]+([\ A-Za-z][A-Za-z\'\-]+)*/
//const nameRegx = /^[A-Za-zÆØÅæøåÄ][A-Za-z\'\-\Wæøå]+([\ A-Za-z][A-Za-z\'\-\Wæøå]+)*/

const nameRegx = /^\S+$/
const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		marginBottom: theme.spacing(3),
		"& .Mui-error": {
			marginLeft: 0,
			marginRight: 0,
		},
		"& .MuiFormLabel-root": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiFormHelperText-contained": {
			marginLeft: 0,
			marginRight: 0,
			color: theme.palette.error.main,
		},
	},
	dividerLine: {
		border: `1px solid ${theme.palette.grey[400]}`,
		marginTop: 0,
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		alignItems: "center",
	},
	formActionsBtn: {
		...theme.typography.fieldValue,
		fontWeight: 500,
		margin: "8px",
		padding: "4px 12px",
		textTransform: "capitalize",
	},
	required: {
		color: theme.palette.error.main,
	},
	textLabel: {
		...theme.typography.fieldValue,
		paddingBottom: 10,
	},
	textField: {
		"& .MuiOutlinedInput-input": {
			//padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},
	},
	userRoleDropdown: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-end",
		padding: "2px",
		gap: "2px",
		borderRadius: "3px",
		fontSize: theme.typography.fontSize,
		"& .MuiSelect-icon": {
			color: theme.palette.grey["D300"],
		},
		"& .MuiInput-input": {
			padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},
		maxWidth: "567px",
	},
	menuItemStyle: {
		borderBottom: `1px solid ${theme.palette.grey["P300"]}`,
	},
	activeBtn: {
		...theme.typography.fieldValue,
		fontWeight: 500,
		margin: "8px",
		padding: "4px 12px",
		textTransform: "capitalize",
		background: theme.palette.primary.dark,
		color: theme.palette.common.white,
		"&:hover": {
			background: theme.palette.primary.dark,
		},
	},
	passwordChange: {
		"& .MuiRadio-colorSecondary.Mui-checked": {
			color: theme.palette.primary.dark,
		},
		"& .MuiTypography-body1": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiFormControlLabel-root": {
			marginLeft: 0,
		},
	},
}))

const ChangePasswordFields = (props) => {
	const theme = useTheme()
	const classes = useStyles()
	const [t] = useTranslation("global")
	const dispatch = useDispatch()
	const [showcurrentPassword, setShowcurrentPassword] = useState(false)
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const { loading, passwordChanged } = useSelector((state) => state?.headerReducers)

	const passwordValidationSchema = yup.object({
		currentPassword: yup.string().required(t("user.formErrors.required")),
		password: yup
			.string()
			.required(t("user.formErrors.required"))
			.matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[(!@#$%^&*.)])[A-Za-z\d(!@#$%^&*.)]{8,20}$/, t("user.formErrors.password")),
		confirmPassword: yup
			.string()
			.required(t("user.formErrors.required"))
			.oneOf([yup.ref("password"), null], t("user.formErrors.passwordMustMatch")),
	})

	const handleFormSubmit = (formData) => {
		const payload = {
			old_password: formData?.currentPassword,
			new_password: formData?.password,
		}
		dispatch(changePassword(payload))
		props?.onClose()
	}

	const changePasswordForm = useFormik({
		initialValues: {
			currentPassword: "",
			password: "",
			confirmPassword: "",
		},
		validationSchema: passwordValidationSchema,
		onSubmit: handleFormSubmit,
	})

	useEffect(() => {
		changePasswordForm?.resetForm()
		dispatch(changePasswordReset())
	}, [])

	return (
		<form className={classes.root} noValidate autoComplete="off" id="create-user" onSubmit={changePasswordForm.handleSubmit}>
			<Box>
				<>
					<FormControl fullWidth className={classes.margin}>
						<TextField
							id="currentPassword"
							type={showcurrentPassword ? "text" : "password"}
							variant="outlined"
							label={`${t("user.formFields.currentPassword")}*`}
							placeholder={t("user.formFields.currentPassword")}
							className={classes.textField}
							value={changePasswordForm.values.currentPassword}
							onChange={changePasswordForm.handleChange}
							onBlur={changePasswordForm.handleBlur}
							error={changePasswordForm.touched.currentPassword && Boolean(changePasswordForm.errors.currentPassword)}
							helperText={changePasswordForm.touched.currentPassword && changePasswordForm.errors.currentPassword}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={() => setShowcurrentPassword(!showcurrentPassword)}
										>
											{showcurrentPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</FormControl>
					<FormControl fullWidth className={classes.margin}>
						<TextField
							id="password"
							type={showPassword ? "text" : "password"}
							variant="outlined"
							label={`${t("user.formFields.newPassword")}*`}
							placeholder={t("user.formFields.newPassword")}
							className={classes.textField}
							value={changePasswordForm.values.password}
							onChange={changePasswordForm.handleChange}
							onBlur={changePasswordForm.handleBlur}
							error={changePasswordForm.touched.password && Boolean(changePasswordForm.errors.password)}
							helperText={changePasswordForm.touched.password && changePasswordForm.errors.password}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
											{showPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</FormControl>
					<FormControl fullWidth className={classes.margin}>
						<TextField
							id="confirmPassword"
							type={showConfirmPassword ? "text" : "password"}
							label={`${t("user.formFields.confirmNewPassword")}*`}
							variant="outlined"
							placeholder={t("user.formFields.confirmNewPassword")}
							className={classes.textField}
							value={changePasswordForm.values.confirmPassword}
							onChange={changePasswordForm.handleChange}
							onBlur={changePasswordForm.handleBlur}
							error={changePasswordForm.touched.confirmPassword && Boolean(changePasswordForm.errors.confirmPassword)}
							helperText={changePasswordForm.touched.confirmPassword && changePasswordForm.errors.confirmPassword}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										>
											{showConfirmPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</FormControl>
				</>
				<hr className={classes.dividerLine} />
				<FormControl className={classes.formActions}>
					<Button variant="contained" size="small" className={classes.formActionsBtn} onClick={() => props?.onClose()}>
						{t("user.formActions.cancel")}
					</Button>
					<Button
						variant="contained"
						size="small"
						className={changePasswordForm.isValid ? classes.activeBtn : classes.formActionsBtn}
						type="submit"
						disabled={!changePasswordForm.isValid || loading || changePasswordForm?.values?.currentPassword?.length <= 0}
					>
						{t("user.formActions.update")}
					</Button>
				</FormControl>
			</Box>
		</form>
	)
}

export default ChangePasswordFields
